// The PixelStory townkit renderer: a TypeScript port of the Character Maker's townkit (same templates, same tone
// ramps, same light direction, same ink outline rules, bit for bit), plus what the game needs on top: things you
// hold, attack poses, expressions for conversations, and front-facing portraits (face and torso).
import { HELD, KIT, KIT_DEFAULT, type KitLook } from '../../../shared/kit.js';

const FW = 32, FH = 32;
type Rows = Record<string, [number, string][]>;
type Grid = string[][];
type RGB = [number, number, number];
interface Ramp { hl: RGB; base: RGB; sh: RGB; deep: RGB; ink: RGB }

// ---------- colour (matches Python colorsys + round-half-even, as in the maker)
const pmod = (a: number, b: number) => ((a % b) + b) % b;
function pyround(v: number): number { const f = Math.floor(v), d = v - f; if (d > 0.5) return f + 1; if (d < 0.5) return f; return f % 2 === 0 ? f : f + 1; }
const hexc = (h: string): RGB => { h = h.replace('#', ''); return [0, 2, 4].map(i => parseInt(h.slice(i, i + 2), 16)) as RGB; };
function rgb2hls(r: number, g: number, b: number): [number, number, number] {
  const maxc = Math.max(r, g, b), minc = Math.min(r, g, b), sumc = maxc + minc, rangec = maxc - minc, l = sumc / 2;
  if (minc === maxc) return [0, l, 0];
  const s = l <= 0.5 ? rangec / sumc : rangec / (2.0 - maxc - minc);
  const rc = (maxc - r) / rangec, gc = (maxc - g) / rangec, bc = (maxc - b) / rangec;
  const h = r === maxc ? bc - gc : g === maxc ? 2.0 + rc - bc : 4.0 + gc - rc;
  return [pmod(h / 6.0, 1.0), l, s];
}
function _v(m1: number, m2: number, hue: number): number { hue = pmod(hue, 1.0); if (hue < 1 / 6) return m1 + (m2 - m1) * hue * 6.0; if (hue < 0.5) return m2; if (hue < 2 / 3) return m1 + (m2 - m1) * (2 / 3 - hue) * 6.0; return m1; }
function hls2rgb(h: number, l: number, s: number): [number, number, number] {
  if (s === 0) return [l, l, l];
  const m2 = l <= 0.5 ? l * (1.0 + s) : l + s - (l * s), m1 = 2.0 * l - m2;
  return [_v(m1, m2, h + 1 / 3), _v(m1, m2, h), _v(m1, m2, h - 1 / 3)];
}
const ramps = new Map<string, Ramp>();
function ramp(hx: string, o: { hl?: number; sh?: number; warm?: number; sat?: number } = {}): Ramp {
  const key = hx + JSON.stringify(o); const hit = ramps.get(key); if (hit) return hit;
  const hl = o.hl ?? 0.10, sh = o.sh ?? 0.12, warm = o.warm ?? 0.04, sat = o.sat ?? 0.08;
  const [r, g, b] = hexc(hx).map(v => v / 255);
  const [h, l, s] = rgb2hls(r, g, b);
  const mk = (dl: number, dh: number, ds: number) => hls2rgb(pmod(h + dh, 1), Math.min(.97, Math.max(.03, l + dl)), Math.min(1, Math.max(0, s + ds))).map(v => pyround(v * 255)) as RGB;
  const ty = pmod(0.13 - h + 0.5, 1) - 0.5, tr = pmod(0.97 - h + 0.5, 1) - 0.5;
  const out = { hl: mk(hl, ty * warm * 2, -0.03), base: hexc(hx), sh: mk(-sh, tr * warm * 2, sat), deep: mk(-sh * 2.1, tr * warm * 3, sat * 1.4), ink: mk(-l + 0.10, tr * warm * 3, 0.05) };
  ramps.set(key, out); return out;
}

// ---------- grids
const blank = (h = FH): Grid => Array.from({ length: h }, () => new Array(FW).fill('.'));
function grid(rows: Rows | undefined, mirror: boolean): Grid {
  const g = blank();
  if (rows) for (const k in rows) {
    const y = +k; if (y < 0 || y >= FH) continue;
    for (const [x, run] of rows[k]) for (let i = 0; i < run.length; i++) { const ch = run[i], xx = x + i; if (ch !== '.' && xx >= 0 && xx < FW) g[y][xx] = ch; }
  }
  if (mirror) for (let y = 0; y < FH; y++) for (let i = 0; i < 16; i++) g[y][16 + i] = g[y][15 - i];
  return g;
}
const over = (base: Grid, layer: Grid): Grid => base.map((row, y) => row.map((c, x) => layer[y][x] !== '.' ? layer[y][x] : c));
const fi = (f: number | null) => f === null ? 0 : f + 1;
const SW = [0, 1, 1, 0, -1, -1];
/** Where the visible hand is in each side frame (the arm's column), standing first. */
const SIDE_HAND = [16, 16, 17, 17, 16, 15, 15];

function shiftArm(g: Grid, x0: number, dy: number): Grid {
  if (dy === 0) return g;
  const sel = g.map(r => [r[x0] === 'S', r[x0 + 1] === 'S']);
  const rws: number[] = []; for (let y = 20; y < 26; y++) if (sel[y][0] || sel[y][1]) rws.push(y);
  if (!rws.length) return g;
  const out = g.map(r => r.slice());
  for (const y of rws) for (let i = 0; i < 2; i++) if (sel[y][i]) out[y][x0 + i] = y < 21 ? 'T' : '.';
  for (const y of rws) { const ny = y + dy; if (ny >= 0 && ny < FH) for (let i = 0; i < 2; i++) if (sel[y][i]) out[ny][x0 + i] = 'S'; }
  return out;
}

export type Mood = 'normal' | 'happy' | 'sleepy' | 'shock' | 'talk';
function face(g0: Grid, view: string, mood: Mood): Grid {
  const g = g0.map(r => r.slice());
  const eye: [number, number][] = []; for (let y = 0; y < g.length; y++) for (let x = 0; x < FW; x++) if ('EWG'.includes(g[y][x])) eye.push([y, x]);
  const xs = [...new Set(eye.map(p => p[1]))].sort((a, b) => a - b);
  const blocks: number[] = []; for (const x of xs) if (!blocks.some(b0 => Math.abs(x - b0) <= 1)) blocks.push(x);
  const y0 = eye.length ? Math.min(...eye.map(p => p[0])) : 0;
  const setRow = (y: number, x0: number, x1: number, ch: string) => { if (y < 0 || y >= g.length) return; for (let x = x0; x < Math.min(x1, FW); x++) g[y][x] = ch; };
  const swap = (a: string, b: string) => { for (const r of g) for (let x = 0; x < FW; x++) if (r[x] === a) r[x] = b; };
  if (mood === 'normal') swap('M', 'S');
  else if (mood === 'happy') {
    for (const [y, x] of eye) g[y][x] = 'S';
    for (const x0 of blocks) { setRow(y0 + 1, x0, x0 + 2, 'E'); for (const xx of [x0 - 1, x0 + 2]) if (g[y0 + 2] && g[y0 + 2][xx] === 'S') g[y0 + 2][xx] = 'E'; }
  } else if (mood === 'sleepy') {
    for (const [y, x] of eye) g[y][x] = 'S'; swap('M', 'S');
    for (const x0 of blocks) setRow(y0 + 2, x0, x0 + 2, 'E');
  } else if (mood === 'shock') {
    for (const x0 of blocks) {
      for (let y = y0; y < y0 + 3; y++) setRow(y, x0, x0 + 2, 'W');
      const xe = x0 + (view === 'side' ? 1 : (x0 < 16 ? 1 : 0)); if (g[y0 + 1]) g[y0 + 1][xe] = 'E';
    }
    const ms: [number, number][] = []; for (let y = 0; y < g.length; y++) for (let x = 0; x < FW; x++) if (g[y][x] === 'M') ms.push([y, x]);
    for (const [y, x] of ms) if (y + 1 < FH && g[y + 1][x] === 'S') g[y + 1][x] = 'M';
  }
  return g;
}

function lashes(g: Grid, view: string): Grid {
  const pts: [number, number][] = []; for (let y = 0; y < g.length; y++) for (let x = 0; x < FW; x++) if (g[y][x] === 'E' || g[y][x] === 'W') pts.push([y, x]);
  if (!pts.length) return g;
  const y0 = Math.min(...pts.map(p => p[0])), top = pts.filter(p => p[0] === y0).map(p => p[1]);
  const left = top.filter(x => x < 16), right = top.filter(x => x >= 16);
  const outer = view === 'side' ? [Math.max(...top)] : [Math.min(...left), Math.max(...right)];
  for (const x of outer) { if (!isFinite(x)) continue; const xo = (view === 'side' || x >= 16) ? x + 1 : x - 1; if (g[y0][xo] === 'S') g[y0][xo] = 'E'; }
  return g;
}

const LIGHT = [-0.55, -0.65];
// the maker's regions, plus K (cloak), L (steel), O (wood) and U (lantern glow) for the game's gear
const REGIONS = ['H', 'S', 'T', 'P', 'N', 'R', 'V', 'D', 'Q', 'Y', 'F', 'C', 'K', 'L', 'O', 'U'];
const OUTLINE_ORDER = ['N', 'P', 'D', 'T', 'V', 'Q', 'C', 'K', 'S', 'R', 'F', 'Y', 'O', 'L', 'U', 'H'];
const BASE_OF: Record<string, string> = { h: 'H', A: 'H', s: 'S', t: 'T', p: 'P', r: 'R', v: 'V', d: 'D', q: 'Q', y: 'Y', f: 'F', c: 'C', k: 'K', l: 'L', o: 'O' };
const FACE_PX: Record<string, RGB> = { E: [34, 28, 40], W: [255, 255, 255], G: [92, 96, 120], B: [246, 150, 140], M: [110, 58, 62] };

function paint(g: Grid, pal: Record<string, Ramp>): ImageData {
  const H = g.length, W = FW, out = new Uint8ClampedArray(H * W * 4);
  const put = (y: number, x: number, c: RGB) => { const i = (y * W + x) * 4; out[i] = c[0]; out[i + 1] = c[1]; out[i + 2] = c[2]; out[i + 3] = 255; };
  const region = g.map(r => r.map(c => BASE_OF[c] || c));
  for (const R of REGIONS) {
    if (!pal[R]) continue;
    let n = 0, sx = 0, sy = 0, x0 = 1e9, x1 = -1, y0 = 1e9, y1 = -1;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (region[y][x] === R) { n++; sx += x; sy += y; x0 = Math.min(x0, x); x1 = Math.max(x1, x); y0 = Math.min(y0, y); y1 = Math.max(y1, y); }
    if (!n) continue;
    const cx = sx / n, cy = sy / n, rx = Math.max(1, (x1 - x0 + 1) / 2), ry = Math.max(1, (y1 - y0 + 1) / 2);
    const k = 'TPVDRK'.includes(R) ? 0.35 : 1.0, p = pal[R], cols = [p.hl, p.base, p.sh, p.deep];
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      if (region[y][x] !== R) continue;
      const nx = (x - cx) / rx, ny = (y - cy) / ry, d = nx * LIGHT[0] + ny * LIGHT[1] * k;
      let tone = d > 0.55 ? 0 : d > -0.35 ? 1 : d > -0.85 ? 2 : 3;
      if (R === 'S' || R === 'N' || R === 'F' || R === 'Q') tone = Math.min(2, Math.max(1, tone));
      if (R === 'U') tone = Math.min(1, tone);
      const nbEmpty = (x + 1 < W && region[y][x + 1] === '.') || (y + 1 < H && region[y + 1][x] === '.');
      if (nbEmpty && tone >= 1 && R !== 'U') tone = Math.min(3, tone + 1);
      put(y, x, cols[tone]);
    }
    const lo = R.toLowerCase();
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      if (g[y][x] === lo) put(y, x, R !== 'H' ? p.sh : p.deep);
      if (R === 'H' && g[y][x] === 'A') put(y, x, p.hl);
    }
  }
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
    if (region[y][x] === 'H' && y + 1 < H && region[y + 1][x] === 'S') put(y, x, pal.H.deep);
    if (region[y][x] === 'S' && y > 0 && region[y - 1][x] === 'H' && g[y][x] === 'S') put(y, x, pal.S.sh);
  }
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (FACE_PX[g[y][x]]) { put(y, x, FACE_PX[g[y][x]]); region[y][x] = 'S'; }
  const filled = region.map(r => r.map(c => c !== '.'));
  for (const R of OUTLINE_ORDER) {
    if (!pal[R]) continue;
    for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) {
      if (filled[y][x]) continue;
      const t = (y > 0 && region[y - 1][x] === R) || (y + 1 < H && region[y + 1][x] === R) || (x > 0 && region[y][x - 1] === R) || (x + 1 < W && region[y][x + 1] === R);
      if (t) put(y, x, pal[R].ink);
    }
  }
  for (let y = 0; y < H; y++) for (let x = 0; x < W; x++) if (g[y][x] === 'Z') put(y, x, [232, 234, 242]);   // fishing line: thin, no outline
  return new ImageData(out, W, H);
}

function wearLayer(name: string, view: string, frame: number | null): Grid {
  const w = KIT.WEAR[name] as Record<string, unknown> & { side_frames?: Rows[]; side?: Rows };
  let lay: Grid;
  if (view === 'side') lay = grid(w.side_frames ? w.side_frames[fi(frame)] : w.side, false);
  else lay = grid((w[view] as Rows) || {}, true);
  const full = w[view + '_full'] as Rows | undefined;
  return full ? over(lay, grid(full, false)) : lay;
}

/** Stamp a held thing onto the grid at a hand. */
function stamp(g: Grid, pts: [number, number, string][], hx: number, hy: number, onlyEmpty = false): Grid {
  const out = g.map(r => r.slice());
  for (const [dx, dy, ch] of pts) { const x = hx + dx, y = hy + dy; if (x >= 0 && x < FW && y >= 0 && y < FH && (!onlyEmpty || out[y][x] === '.')) out[y][x] = ch; }
  return out;
}

export type Pose = 'stand' | 'windup' | 'strike' | 'recover';
export interface ComposeOpts { view: 'front' | 'back' | 'side'; frame: number | null; mood: Mood; pose?: Pose }

/** One frame of a character as ImageData (32 x 34: two empty rows on top, feet on row 31). */
export function compose(look: KitLook, o: ComposeOpts): ImageData {
  const L = { ...KIT_DEFAULT, ...look }, view = o.view, frame = o.frame, pose = o.pose ?? 'stand';
  const st = KIT.HAIR[L.style] ?? KIT.HAIR.short, mir = view === 'front' || view === 'back';
  const wear = (L.wear || []).filter(w => KIT.WEAR[w]), held = (L.wear || []).filter(w => HELD[w]);
  let g = grid((st as Record<string, Rows>)[view + '_behind'], mir);
  // things held in the far hand in a side view go behind the body
  const sideHand = SIDE_HAND[fi(frame)];
  if (view === 'side') for (const h of held) if (HELD[h].behindSide) g = stamp(g, HELD[h].side, sideHand - 4, 24);
  let b: Grid;
  if (mir) {
    b = grid(KIT.FRONT_BODY, true);
    b = over(b, grid(KIT.FRONT_LEGS[frame ?? 0], false));
    if (frame !== null && view === 'front') { const sw = SW[frame]; b = shiftArm(b, 10, sw); b = shiftArm(b, 20, -sw); }
  } else b = over(grid(KIT.SIDE_BODY, false), grid(KIT.SIDE_FRAMES[fi(frame)], false));
  if (pose === 'strike' && view === 'side' && !held.some(h => HELD[h].hand === 'main')) b = stamp(b, [[1, -1, 'S'], [2, -1, 'S'], [1, 0, 'S'], [2, 0, 'S']], sideHand, 23);   // a little punch
  g = over(g, b);
  for (const w of wear) if (KIT.WEAR[w].layer !== 'top' && w !== 'scarf') g = over(g, wearLayer(w, view, frame));
  // in a side view the arm hangs in front of the clothes: draw it back over vests, aprons and coats
  if (view === 'side') {
    const arm: number[] = [];
    for (let y = 20; y <= 24; y++) for (let x = 0; x < FW; x++) if (b[y][x] === 'S' || (y === 20 && b[y][x] === 't')) { g[y][x] = b[y][x]; if (b[y][x] === 'S' && !arm.includes(x)) arm.push(x); }
    if (arm.length) {
      const x0 = Math.min(...arm);
      // a shaded edge behind the arm and a hand below the sleeve, so the arm reads against any shirt
      for (let y = 21; y <= 24; y++) if ('TVDQ'.includes(g[y][x0 - 1] ?? '.')) g[y][x0 - 1] = g[y][x0 - 1].toLowerCase();
      for (const x of arm) if (g[25][x] !== '.') g[25][x] = 'S';
    }
  }
  if (view === 'back' && L.style !== 'short') for (let x = 12; x < 20; x++) if (g[17][x] === 'S') g[17][x] = '.';
  g = over(g, grid((st as Record<string, Rows>)[view], mir));
  if (wear.includes('scarf') && view === 'back') g = over(g, wearLayer('scarf', view, frame));
  g = over(g, grid((st as Record<string, Rows>)[view + '_over'], mir));
  g = over(g, grid((st as Record<string, Rows>)[view + '_full'], false));
  if (wear.includes('scarf') && view !== 'back') g = over(g, wearLayer('scarf', view, frame));
  for (const w of wear) if (KIT.WEAR[w].layer === 'top') g = over(g, wearLayer(w, view, frame));
  // held things: your right hand is on the left of the picture from the front, on the right from behind
  for (const h of held) {
    const d = HELD[h], main = d.hand === 'main';
    if (view === 'side') {
      if (d.behindSide) continue;
      const pts = pose === 'strike' && d.strike ? d.strike.side : pose === 'windup' && d.windup ? d.windup.side : d.side;
      g = stamp(g, pts, sideHand, 24);
    } else {
      const sw = view === 'front' && frame !== null ? SW[frame] : 0;
      const leftOfPicture = view === 'front' ? main : !main;
      const pts = pose === 'strike' && d.strike ? d.strike[view] : d[view];
      g = stamp(g, pts, leftOfPicture ? 10 : 21, 24 + (leftOfPicture ? sw : -sw));
    }
  }
  const bob = (frame === null || frame === 0 || frame === 3) ? 0 : -1;
  if (bob) { const n = g.map(r => r.slice()); for (let y = 0; y < 26; y++) n[y] = g[y + 1].slice(); g = n; }
  g = face(g, view, o.mood);
  if (st.lashes && view !== 'back' && (o.mood === 'normal' || o.mood === 'shock')) g = lashes(g, view);
  // a lean into the swing (side view): the whole figure moves a pixel
  if (view === 'side' && (pose === 'strike' || pose === 'windup')) { const dx = pose === 'strike' ? 1 : -1; g = g.map(r => { const n = new Array(FW).fill('.'); for (let x = 0; x < FW; x++) { const nx = x + dx; if (nx >= 0 && nx < FW) n[nx] = r[x]; } return n; }); }
  const col = (v: string, table: Record<string, string>) => table[v] || v;
  const pal: Record<string, Ramp> = {
    H: ramp(col(L.hair, KIT.HAIRS), { hl: 0.13 }), S: ramp(col(L.skin, KIT.SKINS), { hl: 0.05, sh: 0.08, warm: 0.06 }),
    T: ramp(col(L.top, KIT.TOPS)), P: ramp(col(L.bottom, KIT.BOTTOMS)), N: ramp(L.shoe, { hl: 0.12 }), R: ramp(L.ribbon),
    V: ramp(L.vest), D: ramp(L.dress), Q: ramp(L.cloth, { hl: 0.02, sh: 0.09 }), C: ramp(L.cap, { hl: 0.12 }), Y: ramp('#f0c75a', { hl: 0.12 }),
    K: ramp(L.cloak || '#6a8f5a'), L: ramp('#c9ccd6', { hl: 0.12 }), O: ramp('#a0683e'), U: ramp('#ffe9a0', { hl: 0.02, sh: 0.05 }),
  };
  g = [new Array(FW).fill('.'), new Array(FW).fill('.'), ...g];
  return paint(g, pal);
}

// ---------- conversations: the old engine's expressions, as townkit moods
const MOOD: Record<string, Mood> = { neutral: 'normal', smile: 'talk', happy: 'happy', laugh: 'happy', excited: 'happy', love: 'happy', wink: 'happy',
  surprised: 'shock', sleepy: 'sleepy', sad: 'sleepy', concerned: 'talk', angry: 'talk', determined: 'talk' };
export const moodOf = (face: string | null | undefined): Mood => (face && MOOD[face]) || 'normal';

/** A swoosh drawn onto the frame as the swing lands (pixel by pixel, so it stays crisp). */
export function slash(c: CanvasRenderingContext2D, view: 'front' | 'back' | 'side', strong: boolean): void {
  c.fillStyle = strong ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.45)';
  const [cx, cy, r, a0, a1] = view === 'side' ? [19, 23, 9, -1.1, 1.1] : view === 'front' ? [16, 26, 9, 0.35, 2.8] : [16, 11, 10, 3.5, 5.9];
  for (let a = a0; a <= a1; a += 0.09) for (const rr of strong ? [r, r - 1] : [r]) {
    const x = Math.round(cx + Math.cos(a) * rr), y = Math.round(cy + Math.sin(a) * rr * (view === 'side' ? 1 : 0.7));
    if (x >= 0 && x < 32 && y >= 0 && y < 34) c.fillRect(x, y, 1, 1);
  }
}

/** Where to crop a front view for a portrait: the face and torso, hats included. */
export const PORTRAIT_CROP = { x: 4, y: 1, w: 24, h: 24 };
